import React, { useCallback } from "react";
import { useMutation } from "react-apollo";
import AddMultipleItemsToRequisitionListMutation from "./AddMultipleItemsToRequisitionListMutation.gql";
import useShouldDisplayCommandStatusTimeout from "theme/modules/Wishlist/AddProductToWishlist/useShouldDisplayCommandStatusTimeout";

/**
 * @typedef {typeof useMutation} UseMutation
 * @typedef {ReturnType<useMutation>} UseMutationReturn
 * @typedef {UseMutationReturn[0]} MutationFunction
 * @typedef {ReturnType<MutationFunction>} MutationFunctionReturn
 */

/**
 * @typedef {object} AddToRequisitionListFunctionProps
 * @param {string} requisitionListId
 * @param {any[]} selectedItems
 */

/**
 * @callback AddToRequisitionListFunction
 * @param {AddToRequisitionListFunctionProps} props
 * @returns {MutationFunctionReturn}
 */

/**
 * @typedef {object} InputProps
 * @property {import('./withAllOptionsSet').NormalizedItems} items
 */

/**
 * @typedef {object} OutputProps
 * @property {AddToRequisitionListFunction} addToRequisitionList
 * @property {string} [addedToRequisitionListId]
 * @property {boolean} [addingToRequisitionList]
 * @property {string} [addToRequisitionListError]
 */

/**
 * @param {React.ComponentType<BaseProps & InputProps & OutputProps>} BaseComponent
 * @returns {React.FunctionComponent<BaseProps & InputProps>}
 * @template BaseProps
 */
const withAddMultipleItemsToRequisitionListMutation =
  (BaseComponent) => (props) => {
    const [
      addMultipleItemsToRequisitionListMutation,
      {
        loading: addingToRequisitionList,
        error: errorAddingToRequsitionList,
        data: addToRequisitionListData,
      },
    ] = useMutation(AddMultipleItemsToRequisitionListMutation);

    const showMutationStatus = useShouldDisplayCommandStatusTimeout(
      2500,
      errorAddingToRequsitionList || addToRequisitionListData,
      addingToRequisitionList
    );

    const addToRequisitionList = useCallback(
      /**
       * @param {string} requisitionListId
       * @param {import('./withAllOptionsSet').NormalizedItems} items
       */
      (requisitionListId, items = props.items) => {
        return addMultipleItemsToRequisitionListMutation({
          variables: {
            id: requisitionListId,
            products: items.map(
              ({
                product,
                selectedConfigurableOptions,
                selectedBundleOptions,
                quantity,
              }) => ({
                sku: product.sku,
                quantity,
                selectedConfigurableOptions: Object.entries(
                  selectedConfigurableOptions
                ).map(([option_id, option_value]) => ({
                  option_id,
                  option_value,
                })),
                selectedBundleOptions: Object.entries(
                  selectedBundleOptions
                ).map(([option_id, { quantity, values }]) => {
                  return {
                    option_id,
                    option_values: values.map((value) => ({ quantity, value })),
                  };
                }),
              })
            ),
          },
        });
      },
      [addMultipleItemsToRequisitionListMutation, props.items]
    );

    const mutationSuccess =
      addToRequisitionListData?.addMultipleItemsToRequisitionList.success;

    let error;
    if (showMutationStatus) {
      if (errorAddingToRequsitionList) {
        error = errorAddingToRequsitionList.message;
      } else if (!mutationSuccess) {
        error =
          addToRequisitionListData?.addMultipleItemsToRequisitionList
            .errorMessage;
      }
    }

    const addedToRequisitionListId =
      showMutationStatus && mutationSuccess
        ? addToRequisitionListData?.addMultipleItemsToRequisitionList
            .requisitionList?.id
        : null;

    return (
      <BaseComponent
        {...props}
        addToRequisitionList={addToRequisitionList}
        addedToRequisitionListId={addedToRequisitionListId}
        addingToRequisitionList={addingToRequisitionList}
        addToRequisitionListError={error}
      />
    );
  };

export default withAddMultipleItemsToRequisitionListMutation;
